import React, {
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { RoutineEditScreenProps } from "../RoutineEditMain/RoutineEditNavigation";
import { RoutineEditChecklistSectionFooter } from "./RoutineEditChecklistSectionFooter";
import { generateId } from "../State/generateId";
import { Connection, Section, Task } from "../State/Routine/types";
import { TaskTypesType } from "../../../types/TaskUIType";
import { RoutineEditChecklistTaskItem } from "./RoutineEditChecklistTaskItem/RoutineEditChecklistTaskItem";
import { FlashList, FlashListRef } from "@shopify/flash-list";
import { RoutineEditChecklistSectionHeader } from "./RoutineEditChecklistSectionHeader";

import { useIntlTokens } from "../../../components/mobile/useIntlTokens";
import { BackHandler, Keyboard, Platform } from "react-native";
import { useListContentStyle } from "../../../components/mobile/useListContentStyle";
import { useIntl } from "react-intl";

import { useFocusEffect } from "@react-navigation/native";
import { useAlert } from "../../../common/Alert";
import {
  MoreItem,
  useNavigationHeaderOptions,
} from "../../../common/navigationHeaderOptions";

import { KeyboardAwareFlashListScrollComponent } from "@/src/components/mobile/KeyboardAwareFlashListScrollComponent";

import {
  useRoutineEditState,
  useRoutineEditStateSetParamsFunction,
} from "../State/RoutineEditState";

const defaultTaskBase = {
  title: "",
  instructions: "",
  images: [],
};

const defaultTaskCheck = {
  ...defaultTaskBase,
  type: "TaskCheck" as const,
  sentiments: ["POSITIVE" as const, "NEGATIVE" as const],
};

const defaultTaskNumber = {
  ...defaultTaskBase,
  type: "TaskNumber" as const,
  unit: "CUSTOM" as const,
  suffix: "",
};

const defaultTaskText = {
  ...defaultTaskBase,
  type: "TaskText" as const,
};

export function RoutineEditChecklist(
  props: RoutineEditScreenProps<"RoutineEditChecklist">
) {
  const contentContainerStyle = useListContentStyle();

  const flashListRef = useRef<FlashListRef<any>>(null);

  const isOrg = props.route.params.orgWorkspaceId;
  const {
    navigation: { navigate },
  } = props;

  const {
    tasks = [],
    connections = [],
    sections = [],
    selected: selectedState,
    title,
  } = useRoutineEditState();
  const [selected, setSelected] = useState(selectedState);

  const setParams = useRoutineEditStateSetParamsFunction();
  const setTasks = useCallback(
    (func: (prevTasks: Task[]) => Task[]) => {
      setParams((prev) => ({ ...prev, tasks: func(prev.tasks ?? []) }));
    },
    [setParams]
  );
  const setConnections = useCallback(
    (func: (prevConnections: Connection[]) => Connection[]) =>
      setParams((prev) => ({
        ...prev,
        connections: func(prev.connections ?? []),
      })),
    [setParams]
  );
  const setSections = useCallback(
    (func: (prevSections: Section[]) => Section[]) =>
      setParams((prev) => ({ ...prev, sections: func(prev.sections ?? []) })),
    [setParams]
  );

  const numberOfSections = useMemo(() => sections.length, [sections]);
  const connectionMap = useMemo(
    () =>
      connections.reduce(
        (acc, connection) => {
          if (acc[connection.sectionId]) {
            acc[connection.sectionId].push(connection);
          } else {
            acc[connection.sectionId] = [connection];
          }
          return acc;
        },
        {} as Record<string, typeof connections>
      ),
    [connections]
  );

  const taskMap = useMemo(
    () =>
      tasks.reduce(
        (acc, task) => {
          acc[task.id] = task;
          return acc;
        },
        {} as Record<string, Task>
      ),
    [tasks]
  );

  const newSectionId = useRef(generateId());
  const newTaskId = useRef(generateId());
  const sectionData = useMemo(() => {
    return [
      ...sections
        .slice()
        .sort((a, b) => a.ordinal - b.ordinal)
        .flatMap((section) => {
          const sectionConnections = connectionMap[section.id] ?? [];
          const taskItems = sectionConnections
            .slice()
            .sort((a, b) => a.ordinal - b.ordinal)
            .map(({ taskId, sectionId, orgTopic, canBeDisabled }, index) => ({
              id: `${sectionId}${taskId}`,
              sectionId,
              task: { ...taskMap[taskId], id: taskId },
              type: "TASK" as const,
              canBeDisabled: section.orgCanBeDisabled
                ? true
                : (canBeDisabled ?? false),
              orgTopic,
              selectedTask:
                selected?.type === "TASK" && selected.taskId === taskId,
              isFirst: index === 0,
              isLast: sectionConnections.length - 1 === index,
              autoFocus:
                selected?.type === "TASK" &&
                selected.taskId === taskId &&
                selected.autoFocus,
            }));
          return [
            {
              id: section.id,
              selected:
                selected?.type === "SECTION" && selected.id === section.id,
              autoFocus:
                selected?.type === "SECTION" &&
                selected.id === section.id &&
                selected.autoFocus,
              type: "SECTION" as const,
              section,
              numberOfTasks: sectionConnections.length,
            },
            ...taskItems,
            {
              id: `footer-${section.id}`,
              type: "FOOTER" as const,
              sectionId: section.id,
              numberOfTasks: sectionConnections.length,
              newTaskId: newTaskId.current,
            },
          ];
        }),
    ];
  }, [connectionMap, sections, selected, taskMap]);

  const taskCreate = useCallback(
    ({
      sectionId,
      taskId,
      type,
    }: {
      sectionId: string;
      taskId: string;
      type?: TaskTypesType;
      index: number;
    }) => {
      newTaskId.current = generateId();
      const task = {
        id: taskId,
        ...(type === "TaskText"
          ? defaultTaskText
          : type === "TaskNumber"
            ? defaultTaskNumber
            : defaultTaskCheck),
      };
      setTasks((_tasks) => [..._tasks, task]);
      setConnections((_connections) => [
        ..._connections,
        {
          taskId,
          sectionId,
          value: 0,
          ordinal: _connections.filter((c) => c.sectionId === sectionId).length,
          orgTopic: null,
        },
      ]);
      setSelected({ type: "TASK", sectionId, taskId, autoFocus: true });

      if (sectionData[sectionData.length - 1].sectionId === sectionId) {
        flashListRef.current?.scrollToEnd({
          animated: true,
        });
      }
    },
    [sectionData, setConnections, setTasks]
  );

  const sectionCreate = useCallback(() => {
    const id = newSectionId.current;
    newSectionId.current = generateId();
    setSections((_sections) => [
      ..._sections,
      {
        id,
        title: "",
        ordinal: _sections.length,
        assignedTeam: null,
        orgTopic: null,
        orgCanBeDisabled: null,
      },
    ]);
    setSelected({ type: "SECTION", id, autoFocus: true });
    flashListRef.current?.scrollToEnd({
      animated: true,
    });

    return id;
  }, [setSections]);

  const intl_tokens = useIntlTokens();
  const intl = useIntl();

  const navigateDeprecatedToOrderSections = useCallback(() => {
    Keyboard.dismiss();
    navigate("RoutineEditChecklistOrderSections");
  }, [navigate]);

  const onGoBack = useCallback(() => {
    Keyboard.dismiss();
    props.navigation.pop();
  }, [props.navigation]);

  useFocusEffect(
    React.useCallback(() => {
      const onBackPress = () => {
        onGoBack();
        return true;
      };

      if (Platform.OS !== "web") {
        const subscription = BackHandler.addEventListener(
          "hardwareBackPress",
          onBackPress
        );
        return () => subscription.remove();
      }
    }, [onGoBack])
  );

  const backLabel = useMemo(() => {
    if (title.length > 25) {
      return title.slice().substring(0, 25) + "...";
    }
    return title;
  }, [title]);

  const moreOptions = useMemo(() => {
    const options: MoreItem[] = [
      {
        onPress: () => sectionCreate(),
        icon: "add",
        label: intl.formatMessage({
          defaultMessage: "Create Section",
          description: "RoutineEditChecklist create section option label",
        }),
      },
    ];
    if (numberOfSections > 1) {
      options.push({
        onPress: () => navigateDeprecatedToOrderSections(),
        label: intl.formatMessage({
          defaultMessage: "Order Sections",
          description: "RoutineEditChecklist order sections option label",
        }),
        icon: "reorder",
      });
    }

    return options;
  }, [
    intl,
    navigateDeprecatedToOrderSections,
    numberOfSections,
    sectionCreate,
  ]);

  const getOptions = useNavigationHeaderOptions();
  useLayoutEffect(() => {
    props.navigation.setOptions(
      getOptions({
        type: "DONE",
        more: moreOptions,
        defaultContentStyle: true,
        onDone() {
          onGoBack();
        },
      })
    );
  }, [backLabel, getOptions, moreOptions, onGoBack, props.navigation]);

  const onEditSectionIssueTeam = useCallback(
    (sectionId: string) => {
      Keyboard.dismiss();
      navigate("RoutineEditChecklistSectionIssueTeam", {
        sectionId,
      });
    },
    [navigate]
  );

  const onOrderTasks = useCallback(
    (sectionId: string) => {
      Keyboard.dismiss();
      navigate("RoutineEditChecklistSectionOrderTasks", {
        sectionId,
      });
    },
    [navigate]
  );

  const onAddExistingTask = useCallback(
    (sectionId: string) => {
      Keyboard.dismiss();
      navigate("RoutineEditChecklistSectionAddExistingTasks", {
        sectionId,
      });
    },
    [navigate]
  );

  const onPressTaskMore = useCallback(
    (taskId: string) => {
      Keyboard.dismiss();
      navigate("RoutineEditChecklistTask", {
        taskId,
        routineId: props.route.params.routineId,
        orgWorkspaceId: props.route.params.orgWorkspaceId,
      });
    },
    [navigate, props.route.params.routineId, props.route.params.orgWorkspaceId]
  );

  const onAddImage = useCallback(
    (
      taskId: string,
      image: { id: string; uri: string; width: number; height: number }
    ) => {
      setTasks((prevTasks) =>
        prevTasks.map((t) =>
          t.id === taskId ? { ...t, images: [...t.images, image] } : t
        )
      );
    },
    [setTasks]
  );

  const onSetInstructions = useCallback(
    (taskId: string, instructions: string) => {
      setTasks((prevTasks) =>
        prevTasks.map((t) => (t.id === taskId ? { ...t, instructions } : t))
      );
    },
    [setTasks]
  );

  const onSetTitle = useCallback(
    (taskId: string, title: string) => {
      setTasks((prevTasks) =>
        prevTasks.map((t) => (t.id === taskId ? { ...t, title } : t))
      );
    },
    [setTasks]
  );

  const onRemoveTaskImage = useCallback(
    (taskId: string, imageId: string) => {
      setTasks((_tasks) =>
        _tasks.map((_task) =>
          _task.id === taskId
            ? {
                ..._task,
                images: _task.images.filter(({ id }) => id !== imageId),
              }
            : _task
        )
      );
    },
    [setTasks]
  );

  const _onRemoveConnection = useCallback(
    (item: { sectionId: string; taskId: string }) => {
      setConnections((_connections) => {
        const sectionConnections = _connections
          .filter(
            ({ sectionId, taskId }) =>
              sectionId === item.sectionId && taskId !== item.taskId
          )
          .sort((a, b) => a.ordinal - b.ordinal)
          .map((c, i) => ({ ...c, ordinal: i }));
        return [
          ..._connections.filter(
            ({ sectionId }) => sectionId !== item.sectionId
          ),
          ...sectionConnections,
        ];
      });
    },
    [setConnections]
  );

  const Alert = useAlert();

  const onRemoveTask = useCallback(
    (item: {
      taskId: string;
      sectionId: string;
      isOnlyTaskInstance: boolean;
    }) => {
      if (!item.isOnlyTaskInstance) {
        _onRemoveConnection(item);
      } else {
        Alert.alert(
          intl.formatMessage({
            defaultMessage: "Remove Task",
            description: "Checklist Edit: remove task alert title",
          }),
          intl.formatMessage({
            defaultMessage: "Are you sure you want to remove this task?",
            description: "Checklist Edit: remove task alert message",
          }),
          [
            { text: intl_tokens.Cancel },
            {
              text: intl_tokens.Remove,
              style: "destructive",
              onPress: () => {
                _onRemoveConnection(item);
                setTasks((_tasks) =>
                  _tasks.filter((_task) => _task.id !== item.taskId)
                );
              },
            },
          ]
        );
      }
    },
    [
      Alert,
      _onRemoveConnection,
      intl,
      intl_tokens.Cancel,
      intl_tokens.Remove,
      setTasks,
    ]
  );

  const onSetSelectedTask = useCallback(
    (item: { taskId: string; sectionId: string }, index: number) => {
      setSelected({
        type: "TASK",
        taskId: item.taskId,
        sectionId: item.sectionId,
      });
    },
    []
  );

  const onSetSelectedSection = useCallback((sectionId: string) => {
    setSelected({
      type: "SECTION",
      id: sectionId,
    });
  }, []);

  const onSetSectionTitle = useCallback(
    (sectionId: string, title: string) => {
      setSections((prevSections) =>
        prevSections.map((s) => (s.id === sectionId ? { ...s, title } : s))
      );
    },
    [setSections]
  );

  const _onDeleteSection = useCallback(
    (sectionId: string) =>
      Alert.alert(
        intl.formatMessage({
          defaultMessage: "Do you want to remove the section?",
          description: "RoutineEditChecklist remove section alert title",
        }),
        undefined,
        [
          { text: intl_tokens.Cancel },
          {
            text: intl_tokens.Remove,
            style: "destructive",
            onPress: () => {
              setSections((_sections) =>
                _sections
                  .filter((_section) => _section.id !== sectionId)
                  .sort((a, b) => a.ordinal - b.ordinal)
                  .map((s, i) => ({ ...s, ordinal: i }))
              );
              setConnections((_connections) =>
                _connections.filter(
                  (_connection) => _connection.sectionId !== sectionId
                )
              );
            },
          },
        ]
      ),
    [
      Alert,
      intl,
      intl_tokens.Cancel,
      intl_tokens.Remove,
      setConnections,
      setSections,
    ]
  );

  const onDeleteSection = useMemo(
    () => (numberOfSections > 1 ? _onDeleteSection : undefined),
    [numberOfSections, _onDeleteSection]
  );

  const onOrgEditSectionSettings = useCallback(
    (sectionId: string) => {
      if (!props.route.params.orgWorkspaceId) {
        return;
      }
      navigate("WorkspaceRoutineEditSectionSettings", {
        orgWorkspaceId: props.route.params.orgWorkspaceId,
        sectionId,
      });
    },
    [navigate, props.route.params.orgWorkspaceId]
  );

  const renderItem = useCallback(
    ({
      item,
      index,
    }: {
      item: (typeof sectionData)[number];
      index: number;
    }) => {
      switch (item.type) {
        case "FOOTER": {
          return (
            <RoutineEditChecklistSectionFooter
              newTaskId={item.newTaskId}
              sectionId={item.sectionId}
              index={index}
              onCreateTask={taskCreate}
              onOrderTasks={item.numberOfTasks > 1 ? onOrderTasks : undefined}
              onAddExistingTask={
                numberOfSections > 1 && item.numberOfTasks < tasks.length
                  ? onAddExistingTask
                  : undefined
              }
            />
          );
        }
        case "SECTION": {
          return (
            <RoutineEditChecklistSectionHeader
              index={index}
              itemId={item.id}
              assignedIssueTeam={item.section.assignedTeam}
              autoFocus={item.autoFocus}
              selected={item.selected}
              numberOfTasks={item.numberOfTasks}
              sectionId={item.section.id}
              title={item.section.title}
              onSetSelectedItem={onSetSelectedSection}
              onEditIssueTeam={onEditSectionIssueTeam}
              onOrgEditSectionSettings={onOrgEditSectionSettings}
              onDeleteSection={onDeleteSection}
              onSetTitle={onSetSectionTitle}
              isOrg={!!isOrg}
              orgCanBeDisabled={item.section.orgCanBeDisabled ?? false}
              orgTopic={item.section.orgTopic}
            />
          );
        }
        case "TASK": {
          const isOnlyTaskInstance =
            connections.filter(
              (connection) => connection.taskId === item.task.id
            ).length === 1;
          return (
            <RoutineEditChecklistTaskItem
              orgTopic={item.orgTopic}
              autoFocus={item.autoFocus}
              index={index}
              canBeDisabled={item.canBeDisabled ?? false}
              itemType={item.type}
              isOnlyTaskInstance={isOnlyTaskInstance}
              onAddImage={onAddImage}
              onRemoveImage={onRemoveTaskImage}
              onRemoveItem={onRemoveTask}
              onPressTaskMore={onPressTaskMore}
              onSetInstructions={onSetInstructions}
              onSetTitle={onSetTitle}
              isFirst={item.isFirst}
              sectionId={item.sectionId}
              isLast={item.isLast}
              taskId={item.task.id}
              images={item.task.images}
              instructions={item.task.instructions}
              taskType={
                item.task.type === "TaskCheck"
                  ? item.task.sentiments.length === 1
                    ? "CHECK"
                    : "OPTIONS"
                  : item.task.type === "TaskNumber"
                    ? "NUMBER"
                    : "TEXT"
              }
              title={item.task.title}
              onSetSelected={onSetSelectedTask}
              selectedTask={item.selectedTask}
            />
          );
        }
      }
    },
    [
      taskCreate,
      onOrderTasks,
      numberOfSections,
      tasks.length,
      onAddExistingTask,
      onSetSelectedSection,
      onEditSectionIssueTeam,
      onOrgEditSectionSettings,
      onDeleteSection,
      onSetSectionTitle,
      isOrg,
      connections,
      onAddImage,
      onRemoveTaskImage,
      onRemoveTask,
      onPressTaskMore,
      onSetInstructions,
      onSetTitle,
      onSetSelectedTask,
    ]
  );

  return (
    <>
      <FlashList<(typeof sectionData)[number]>
        ref={flashListRef}
        automaticallyAdjustKeyboardInsets={false}
        keyboardShouldPersistTaps="handled"
        contentContainerStyle={contentContainerStyle}
        data={sectionData}
        keyExtractor={(item) => item.id}
        renderScrollComponent={KeyboardAwareFlashListScrollComponent}
        renderItem={renderItem}
        getItemType={(item) => item.type}
      />
    </>
  );
}
